import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { LayoutGrid, Bot, Wrench, Mail, Puzzle } from 'lucide-react';
import { DashboardTemplatesSettings } from './DashboardTemplatesSettings';
import { EmailTemplatesSettings } from './EmailTemplatesSettings';
import { SkillTemplatesSettings } from './SkillTemplatesSettings';

/**
 * Top-level Templates page. Wraps the per-domain catalogs the platform
 * publishes and organizations instantiate, in tabs. The page title is rendered
 * by the Navbar breadcrumb, so the body starts directly with the tab bar in the
 * upper-left — matching the layout of /admin/queues and other admin pages.
 *
 * Dashboards, Emails and Skills are wired up; Agents and Integrations render a
 * "coming soon" placeholder so the navigation reflects the intended structure
 * while we backfill those editors.
 */
export function TemplatesSettings() {
  const { t } = useTranslation();

  return (
    <div className="w-full">
      <Tabs defaultValue="dashboards" className="w-full">
        <div className="flex items-center justify-between border-b">
          <TabsList>
            <TabsTrigger value="dashboards">
              <LayoutGrid className="h-4 w-4 mr-2" />
              {t('settings.templates.tabs.dashboards', 'Dashboards')}
            </TabsTrigger>
            <TabsTrigger value="integrations" disabled>
              <Wrench className="h-4 w-4 mr-2" />
              {t('settings.templates.tabs.integrations', 'Tools')}
            </TabsTrigger>
            <TabsTrigger value="agents" disabled>
              <Bot className="h-4 w-4 mr-2" />
              {t('settings.templates.tabs.agents', 'Workflows')}
            </TabsTrigger>
            <TabsTrigger value="skills">
              <Puzzle className="h-4 w-4 mr-2" />
              {t('settings.templates.tabs.skills', 'Skills')}
            </TabsTrigger>
            <TabsTrigger value="emails">
              <Mail className="h-4 w-4 mr-2" />
              {t('settings.templates.tabs.emails', 'Emails')}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="dashboards" className="mt-4 w-full px-4">
          <DashboardTemplatesSettings />
        </TabsContent>
        <TabsContent value="integrations" className="mt-4 w-full px-4 py-12">
          <ComingSoon label={t('settings.templates.comingSoon', 'Em breve')} />
        </TabsContent>
        <TabsContent value="agents" className="mt-4 w-full px-4 py-12">
          <ComingSoon label={t('settings.templates.comingSoon', 'Em breve')} />
        </TabsContent>
        <TabsContent value="skills" className="mt-4 w-full px-4">
          <SkillTemplatesSettings />
        </TabsContent>
        <TabsContent value="emails" className="mt-4 w-full px-4">
          <EmailTemplatesSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-2 py-12 text-muted-foreground">
      <span className="text-sm">{label}</span>
    </div>
  );
}
